import { useState, useEffect } from 'react';
import { FileText, BarChart3, Eye, TrendingUp } from 'lucide-react';
import { authFetch } from '../../lib/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="p-5 rounded-xl border border-white/5 bg-[#0f0f17]" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <p className="text-[#71717a] text-sm">{label}</p>
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="text-2xl font-bold text-[#f5f5f5]">{value}</p>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    authFetch(`${API_URL}/api/analytics/summary`)
      .then(res => res.json())
      .then(setStats)
      .catch(() => {});

    authFetch(`${API_URL}/api/demo-requests`)
      .then(res => res.json())
      .then(data => setLeads(Array.isArray(data) ? data.slice(0, 5) : []))
      .catch(() => {});
  }, []);

  return (
    <div className="p-6 md:p-8" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-[#f5f5f5] mb-6">Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Demandes total" value={stats?.total_requests ?? '-'} icon={FileText} color="bg-[#3b82f6]/10 text-[#3b82f6]" />
        <StatCard label="Ce mois" value={stats?.month_requests ?? '-'} icon={TrendingUp} color="bg-green-500/10 text-green-400" />
        <StatCard label="Vues aujourd'hui" value={stats?.today_views ?? '-'} icon={Eye} color="bg-purple-500/10 text-purple-400" />
        <StatCard label="Vues cette semaine" value={stats?.week_views ?? '-'} icon={BarChart3} color="bg-amber-500/10 text-amber-400" />
      </div>

      {/* Recent Leads */}
      <div className="rounded-xl border border-white/5 bg-[#0f0f17] overflow-hidden">
        <div className="p-5 border-b border-white/5">
          <h2 className="text-[#f5f5f5] font-semibold">Dernières demandes de démo</h2>
        </div>
        {leads.length === 0 ? (
          <p className="p-5 text-[#71717a] text-sm">Aucune demande pour le moment.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {leads.map((lead) => (
              <div key={lead.id} className="px-5 py-3 flex items-center justify-between" data-testid={`lead-${lead.id}`}>
                <div>
                  <p className="text-[#f5f5f5] text-sm font-medium">{lead.name}</p>
                  <p className="text-[#71717a] text-xs">{lead.company} — {lead.employee_count} collaborateurs</p>
                </div>
                <div className="text-right">
                  <p className="text-[#71717a] text-xs">{lead.email}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    lead.status === 'new' ? 'bg-[#3b82f6]/10 text-[#3b82f6]' :
                    lead.status === 'contacted' ? 'bg-amber-500/10 text-amber-400' :
                    lead.status === 'done' ? 'bg-green-500/10 text-green-400' : 'bg-[#1a1a2e] text-[#71717a]'
                  }`}>
                    {lead.status === 'new' ? 'Nouveau' : lead.status === 'contacted' ? 'Contacté' : lead.status === 'done' ? 'Terminé' : lead.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top Pages */}
      {stats?.pages?.length > 0 && (
        <div className="mt-6 rounded-xl border border-white/5 bg-[#0f0f17] overflow-hidden">
          <div className="p-5 border-b border-white/5">
            <h2 className="text-[#f5f5f5] font-semibold">Pages les plus vues (30 jours)</h2>
          </div>
          <div className="divide-y divide-white/5">
            {stats.pages.map((p, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between">
                <p className="text-[#a1a1aa] text-sm">{p.page || '/'}</p>
                <p className="text-[#f5f5f5] text-sm font-medium">{p.views} vues</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
